import { StandardEvents } from '@shopify/events';

/** V36.27: the existing header Catalogue link resumes this tab's listing.
 * Native filters and navigation remain responsible for rendering the results.
 * No automatic redirect: an explicit unfiltered catalogue visit resets memory.
 */
const normalizePath = path => path.replace(/\/+$/, '') || '/';
const catalogue = new URL(document.body.dataset.djCatalogueUrl || '/collections/catalogue', location.origin);
const localeRoot = window.Shopify?.routes?.root || '/';
const plainPath = normalizePath(catalogue.pathname);
if (localeRoot.startsWith('/') && !localeRoot.startsWith('//') && localeRoot !== '/') {
  const prefix = normalizePath(localeRoot);
  if (!catalogue.pathname.startsWith(`${prefix}/`)) catalogue.pathname = prefix + catalogue.pathname;
}
const cataloguePath = normalizePath(catalogue.pathname);
const storageKey = `dj-catalogue-filters-v1:${cataloguePath}`;
const isCatalogue = url => url.origin === location.origin && [plainPath, cataloguePath].includes(normalizePath(url.pathname));

// Keep filter values (including repeated choices), sort and pagination only.
// Preview theme ids, section requests and tracking parameters are never stored.
function selection(search) {
  if (typeof search !== 'string' || search.length > 16000) return '';
  const result = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (!value) continue;
    if (/^filter\.[a-z0-9_.-]+$/i.test(key) || key === 'sort_by' || key === 'dj_vehicle_make') result.append(key, value);
    else if (key === 'page' && /^[1-9]\d*$/.test(value)) result.set(key, value);
  }
  return result.toString();
}

let remembered = '';
function readSelection() {
  try { remembered = selection(sessionStorage.getItem(storageKey) || ''); }
  catch { /* The current page can still work when browser storage is blocked. */ }
  return remembered;
}

function rememberCatalogue() {
  const current = new URL(location.href);
  if (!isCatalogue(current) || current.searchParams.get('view') === 'vehicule') return false;
  remembered = selection(current.search);
  try {
    if (remembered) sessionStorage.setItem(storageKey, remembered);
    else sessionStorage.removeItem(storageKey);
  } catch { /* Keep this page's memory; do not interrupt ordinary navigation. */ }
  return true;
}

function updateMenuLinks() {
  const params = new URLSearchParams(remembered);
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  for (const link of document.querySelectorAll('#header-group a[href]')) {
    const original = link.getAttribute('data-dj-catalogue-base-href') || link.getAttribute('href');
    let target;
    try { target = new URL(original, location.origin); } catch { continue; }
    if (!isCatalogue(target) || target.hash || selection(target.search)) continue;
    // Explicit prefiltered menu entries are untouched. Store the generic href
    // on the node so the mobile/overflow menu can safely clone the same link.
    if (!link.hasAttribute('data-dj-catalogue-base-href')) link.setAttribute('data-dj-catalogue-base-href', original);
    target.pathname = cataloguePath;
    for (const [key, value] of params) target.searchParams.append(key, value);
    if (preview) target.searchParams.set('preview_theme_id', preview);
    const href = target.pathname + target.search;
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  }
}

function sync() {
  if (!rememberCatalogue()) readSelection();
  updateMenuLinks();
}

document.addEventListener(StandardEvents.collectionUpdate, sync);
window.addEventListener('pageshow', sync);
window.addEventListener('popstate', sync);
window.addEventListener('pagehide', rememberCatalogue);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') rememberCatalogue();
});
// Capture the latest URL before following a product/menu link, including
// keyboard activation, middle clicks and opening a link in a new tab.
for (const type of ['pointerdown', 'click', 'auxclick', 'contextmenu']) {
  document.addEventListener(type, event => {
    if (event.target instanceof Element && event.target.closest('a[href]')) sync();
  }, true);
}

const header = document.getElementById('header-group');
if (header) new MutationObserver(updateMenuLinks).observe(header, { childList: true, subtree: true });
document.addEventListener('shopify:section:load', sync);
sync();
